import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Users,
  BookOpen,
  FileText,
  BarChart2,
  CheckCircle2,
  AlertTriangle,
  Mail,
  Clock3,
  CalendarDays,
  ClipboardCheck,
  MessageSquare,
  LayoutGrid,
  ArrowRight,
  Bell,
  Activity,
  GraduationCap,
  Loader2
} from "lucide-react";
import { getApiUrl } from "../../config/api";
import "../../styles/DashboardLayout.css";
import { useTenant } from "../../context/TenantContext";

interface DashboardClass {
  id: number;
  name: string;
  studentCount: number;
}

interface DashboardSubject {
  id: number;
  name: string;
  classCount?: number;
}

interface TimetableItem {
  id: number;
  subject: string;
  className: string;
  time: string;
  room?: string | null;
}

interface ActivityItem {
  createdAt: string;
  action: string;
  entityName?: string | null;
}

interface MessageItem {
  id: number;
  content: string;
  createdAt: string;
  senderName?: string | null;
  subject?: string | null;
}

interface NoticeItem {
  id: string;
  title: string;
  message: string;
  createdAt: string;
  audience?: string;
}

interface TeacherDashboardResponse {
  totalStudents: number;
  classes: DashboardClass[];
  subjects: DashboardSubject[];
  metrics: {
    activeClasses: number;
    assignments: number;
    averageScore: number;
    pendingResults: number;
    attendanceNotMarkedToday: number;
    unreadMessages: number;
    nextClass: string;
  };
  timetableToday: TimetableItem[];
  workload: {
    classesAssigned: number;
    subjectsAssigned: number;
    resultsPendingEntry: number;
    assignmentsPendingReview: number;
    attendanceCompletionRate: number;
  };
  recentActivity: ActivityItem[];
  notices: NoticeItem[];
  messages: {
    unreadCount: number;
    latest: MessageItem[];
  };
}

interface SummaryCard {
  label: string;
  value: number | string;
  icon: React.ElementType;
  accent: string;
  compact?: boolean;
}

const TeacherDashboard: React.FC = () => {
  const userName = localStorage.getItem("userName") || "Teacher";
  const token = localStorage.getItem("token") || "";
  const [dashboard, setDashboard] = useState<TeacherDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { hasModule } = useTenant();

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get<TeacherDashboardResponse>(getApiUrl("teacher/dashboard"), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDashboard(res.data);
      } catch (err: any) {
        setError(err.response?.data?.message || "Failed to load teacher dashboard.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [token]);

  const formatDateTime = (value?: string) => {
    if (!value) return "Just now";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("en-ZM", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const canUse = (moduleKey?: string) => !moduleKey || hasModule(moduleKey);
  const summaryCards: SummaryCard[] = [
    {
      label: "Total Students",
      value: dashboard?.totalStudents || 0,
      icon: Users,
      accent: "bg-blue-50 text-blue-600"
    },
    {
      label: "Active Classes",
      value: dashboard?.metrics.activeClasses || 0,
      icon: GraduationCap,
      accent: "bg-teal-50 text-teal-600"
    },
    {
      label: "Assignments",
      value: dashboard?.metrics.assignments || 0,
      icon: FileText,
      accent: "bg-violet-50 text-violet-600"
    },
    {
      label: "Average Score",
      value: `${dashboard?.metrics.averageScore || 0}%`,
      icon: BarChart2,
      accent: "bg-amber-50 text-amber-600"
    },
    {
      label: "Pending Results",
      value: dashboard?.metrics.pendingResults || 0,
      icon: AlertTriangle,
      accent: "bg-rose-50 text-rose-600"
    }
  ];

  if (canUse("attendance")) {
    summaryCards.push({
      label: "Attendance Not Marked",
      value: dashboard?.metrics.attendanceNotMarkedToday || 0,
      icon: ClipboardCheck,
      accent: "bg-orange-50 text-orange-600"
    });
  }

  if (canUse("messaging")) {
    summaryCards.push({
      label: "Unread Messages",
      value: dashboard?.metrics.unreadMessages || 0,
      icon: Mail,
      accent: "bg-sky-50 text-sky-600"
    });
  }

  if (canUse("timetable")) {
    summaryCards.push({
      label: "Next Class",
      value: dashboard?.metrics.nextClass || "No upcoming lesson today",
      icon: Clock3,
      accent: "bg-emerald-50 text-emerald-600",
      compact: true
    });
  }

  const quickActions = [
    { label: "Record Assessments", to: "/record-assessments", Icon: FileText, tone: "hover:bg-blue-50 hover:border-blue-100", module: "results" },
    { label: "View Results", to: "/view-results", Icon: BarChart2, tone: "hover:bg-teal-50 hover:border-teal-100", module: "results" },
    { label: "Mark Attendance", to: "/attendance", Icon: ClipboardCheck, tone: "hover:bg-violet-50 hover:border-violet-100", module: "attendance" },
    { label: "Open Messages", to: "/messages", Icon: MessageSquare, tone: "hover:bg-amber-50 hover:border-amber-100", module: "messaging" },
    { label: "View Timetable", to: "/timetable", Icon: CalendarDays, tone: "hover:bg-sky-50 hover:border-sky-100", module: "timetable" },
    { label: "My Classes", to: "/classes", Icon: LayoutGrid, tone: "hover:bg-emerald-50 hover:border-emerald-100", module: "classes" }
  ].filter(({ module }) => canUse(module));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-vh-100 bg-white">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">Loading teacher dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <main className="dashboard-main p-8 bg-slate-50/40">
        <section className="mb-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <h1 className="text-4xl font-black text-slate-800 tracking-tight">Teacher Dashboard</h1>
            <p className="text-slate-500 mt-2 font-medium">
              Welcome back, {userName}. Your classes, lessons, and pending work are organized here.
            </p>
          </div>
          <div className="bg-white border border-slate-100 rounded-2xl px-5 py-4 shadow-sm max-w-md">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">Next Class</p>
            <p className="text-sm font-semibold text-slate-800">{dashboard?.metrics.nextClass || "No upcoming lesson today"}</p>
          </div>
        </section>

        {error && (
          <div className="mb-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-red-700 font-medium">
            {error}
          </div>
        )}

        <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-8">
          {summaryCards.map((card) => (
            <div key={card.label} className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${card.accent}`}>
                  <card.icon size={21} />
                </div>
              </div>
              <h3 className={`font-bold text-slate-900 ${card.compact ? "text-sm leading-6" : "text-3xl"}`}>{card.value}</h3>
              <p className="text-slate-500 text-sm font-medium mt-1">{card.label}</p>
            </div>
          ))}
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
          {canUse("timetable") && <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <div className="flex items-center justify-between gap-4 mb-5">
              <div>
                <h2 className="text-xl font-bold text-slate-800">Today's Teaching Overview</h2>
                <p className="text-sm text-slate-500 mt-1">Your lessons for today and the next class in line.</p>
              </div>
              <Link to="/timetable" className="inline-flex items-center gap-2 text-sm font-semibold text-teal-700 hover:text-teal-800">
                Open Timetable <ArrowRight size={16} />
              </Link>
            </div>
            <div className="space-y-3">
              {(dashboard?.timetableToday || []).length === 0 && (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                  No lessons scheduled for today.
                </div>
              )}
              {(dashboard?.timetableToday || []).map((lesson) => (
                <div key={lesson.id} className="rounded-2xl border border-slate-100 bg-slate-50 px-5 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <p className="text-base font-semibold text-slate-800">{lesson.subject}</p>
                    <p className="text-sm text-slate-500">{lesson.className}</p>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-600 font-medium">
                    <span>{lesson.time}</span>
                    <span>{lesson.room || "Room not set"}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>}

          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-5">
              <Activity size={18} className="text-slate-600" />
              <h2 className="text-xl font-bold text-slate-800">Workload Snapshot</h2>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-slate-500 text-sm">Classes assigned</span>
                <span className="font-semibold text-slate-800">{dashboard?.workload.classesAssigned || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500 text-sm">Subjects assigned</span>
                <span className="font-semibold text-slate-800">{dashboard?.workload.subjectsAssigned || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500 text-sm">Results pending entry</span>
                <span className="font-semibold text-slate-800">{dashboard?.workload.resultsPendingEntry || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500 text-sm">Assignments pending review</span>
                <span className="font-semibold text-slate-800">{dashboard?.workload.assignmentsPendingReview || 0}</span>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-500 text-sm">Attendance completion this week</span>
                  <span className="font-semibold text-slate-800">{dashboard?.workload.attendanceCompletionRate || 0}%</span>
                </div>
                <div className="w-full h-2.5 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-500"
                    style={{ width: `${dashboard?.workload.attendanceCompletionRate || 0}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
          {quickActions.map(({ label, to, Icon, tone }) => (
            <Link
              key={label}
              to={to}
              className={`bg-white rounded-2xl border border-slate-100 shadow-sm p-5 text-center transition-all ${tone}`}
            >
              <Icon size={24} className="mx-auto mb-3 text-slate-700" />
              <span className="text-sm font-semibold text-slate-700">{label}</span>
            </Link>
          ))}
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 space-y-6">
            {canUse("messaging") && <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              <div className="flex items-center justify-between gap-4 mb-5">
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Subjects Taught</h2>
                  <p className="text-sm text-slate-500 mt-1">Compact view of your subjects and how many classes each covers.</p>
                </div>
                <Link to="/view-results" className="text-sm font-semibold text-teal-700 hover:text-teal-800">
                  Open Results
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(dashboard?.subjects || []).length === 0 && (
                  <div className="col-span-full rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                    No subjects assigned yet.
                  </div>
                )}
                {(dashboard?.subjects || []).map((subject) => (
                  <Link
                    key={subject.id}
                    to="/view-results"
                    className="rounded-2xl border border-slate-100 bg-slate-50 px-5 py-4 hover:bg-teal-50 hover:border-teal-100 transition-all"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-800">{subject.name}</p>
                        <p className="text-sm text-slate-500">{subject.classCount || 0} assigned class{(subject.classCount || 0) === 1 ? "" : "es"}</p>
                      </div>
                      <ArrowRight size={16} className="text-slate-400" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>}

            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              <div className="flex items-center gap-2 mb-5">
                <Activity size={18} className="text-slate-600" />
                <h2 className="text-xl font-bold text-slate-800">Recent Activity</h2>
              </div>
              <div className="space-y-3">
                {(dashboard?.recentActivity || []).length === 0 && (
                  <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                    No recent teacher activity recorded yet.
                  </div>
                )}
                {(dashboard?.recentActivity || []).map((item, index) => (
                  <div key={`${item.action}-${item.createdAt}-${index}`} className="rounded-2xl border border-slate-100 bg-slate-50 px-5 py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-semibold text-slate-800">{String(item.action || "Activity").replace(/_/g, " ")}</p>
                        <p className="text-sm text-slate-500">{item.entityName || "Teacher dashboard activity"}</p>
                      </div>
                      <span className="text-xs font-medium text-slate-400 whitespace-nowrap">{formatDateTime(item.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <MessageSquare size={18} className="text-slate-600" />
                  <h2 className="text-xl font-bold text-slate-800">Messages</h2>
                </div>
                <Link to="/messages" className="text-sm font-semibold text-teal-700 hover:text-teal-800">Open</Link>
              </div>
              <div className="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3 mb-4">
                <p className="text-sm text-slate-500">Unread Messages</p>
                <p className="text-2xl font-bold text-slate-900">{dashboard?.messages.unreadCount || 0}</p>
              </div>
              <div className="space-y-3">
                {(dashboard?.messages.latest || []).length === 0 && (
                  <div className="text-sm text-slate-500">No recent messages.</div>
                )}
                {(dashboard?.messages.latest || []).map((message) => (
                  <div key={message.id} className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                    <p className="font-semibold text-slate-800 text-sm">{message.subject || "Message"}</p>
                    <p className="text-sm text-slate-500 mt-1 line-clamp-2">{message.content}</p>
                    <p className="text-xs text-slate-400 mt-2">{message.senderName || "Unknown sender"} · {formatDateTime(message.createdAt)}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <Bell size={18} className="text-slate-600" />
                  <h2 className="text-xl font-bold text-slate-800">Notices & Announcements</h2>
                </div>
              </div>
              <div className="space-y-3">
                {(dashboard?.notices || []).length === 0 && (
                  <div className="text-sm text-slate-500">No notices available right now.</div>
                )}
                {(dashboard?.notices || []).map((notice) => (
                  <div key={notice.id} className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                    <p className="font-semibold text-slate-800 text-sm">{notice.title}</p>
                    <p className="text-sm text-slate-500 mt-1 line-clamp-3">{notice.message}</p>
                    <p className="text-xs text-slate-400 mt-2">{formatDateTime(notice.createdAt)}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-900 text-white rounded-2xl shadow-xl p-6">
              <h2 className="text-xl font-bold mb-2">Class Snapshot</h2>
              <p className="text-slate-400 text-sm mb-5">A quick view of the classes currently assigned to you.</p>
              <div className="space-y-3">
                {(dashboard?.classes || []).length === 0 && (
                  <div className="text-sm text-slate-400">No classes assigned.</div>
                )}
                {(dashboard?.classes || []).map((cls) => (
                  <div key={cls.id} className="border border-slate-800 rounded-xl px-4 py-3 bg-slate-950/30">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-semibold">{cls.name}</span>
                      <span className="text-sm text-slate-400">{cls.studentCount || 0} learners</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default TeacherDashboard;
